import React, { createContext, useContext, useState, useCallback } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { cn } from './Kit';

const { FiCheckCircle, FiAlertCircle, FiInfo, FiX, FiAlertTriangle } = FiIcons;

const ToastContext = createContext(null);

const ICONS = {
  success: { icon: FiCheckCircle, cls: 'text-green-500' },
  error: { icon: FiAlertCircle, cls: 'text-red-500' },
  warning: { icon: FiAlertTriangle, cls: 'text-yellow-500' },
  info: { icon: FiInfo, cls: 'text-blue-500' },
};

const BG = {
  success: 'border-l-green-500',
  error: 'border-l-red-500',
  warning: 'border-l-yellow-500',
  info: 'border-l-blue-500',
};

function ToastItem({ toast, onRemove }) {
  const cfg = ICONS[toast.type] || ICONS.info;
  return (
    <div className={cn(
      'flex items-start gap-3 bg-white rounded-lg shadow-lg border border-gray-200 border-l-4 p-4 w-80 animate-slide-in',
      BG[toast.type]
    )}>
      <SafeIcon icon={cfg.icon} className={cn('w-5 h-5 flex-shrink-0 mt-0.5', cfg.cls)} />
      <div className="flex-1 min-w-0">
        {toast.title && <p className="text-sm font-semibold text-gray-900">{toast.title}</p>}
        {toast.message && <p className="text-xs text-gray-500 mt-0.5">{toast.message}</p>}
      </div>
      <button onClick={() => onRemove(toast.id)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
        <SafeIcon icon={FiX} className="w-4 h-4" />
      </button>
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ type = 'info', title, message, duration = 4000 }) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, type, title, message }]);
    if (duration > 0) {
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onRemove={removeToast} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}